// /onboard/client — Sales-led landing for prospective company clients.
//
// Five stacked sections: nav, hero (2-col), 7-phase process map, dark client
// portal mockup over SAND, inquiry form, shared MarketingFooter. The hero CTA
// smooth-scrolls to the form anchor; the form inserts into public.client_inquiries
// and fires notify-client-inquiry to email Luke + Tyler + the submitter.
//
// Public route — no auth gate, but NOT advertised on the marketing landing.
// Sales shares the URL privately after a discovery call. Noindex meta keeps
// it out of search results.

import type { Href } from "expo-router";
import { COLORS } from "../../lib/brand";
import Head from "expo-router/head";
import { useRouter } from "expo-router";
import { useEffect, useRef, useState } from "react";
import {
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StatusBar,
  Text,
  TextInput,
  useWindowDimensions,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import Svg, { Circle, Line } from "react-native-svg";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { MarketingFooter } from "../../components/marketing/MarketingFooter";
import { supabase } from "../../lib/supabase";
import { useMarketingFonts } from "../../lib/useMarketingFonts";
import { LogoMark, LogoWordmark } from "../../components/brand/Logo";

/* ────────────────────────── Color tokens ────────────────────────── */

const BLACK = "#080808";
const FOREST_DEEP = COLORS.FOREST_DEEP.hex;
const SAND = COLORS.SAND.hex;
const AMBER = COLORS.AMBER.hex;
const AMBER_WARM = COLORS.AMBER_WARM.hex;
const LINEN = COLORS.LINEN.hex;
const SAGE = COLORS.SAGE.hex;
const LIVE_GREEN = COLORS.LIVE_GREEN.hex;
const CHARCOAL = "#222222";

const NAV_HEIGHT = 68;
const NARROW_BREAKPOINT = 860;

const IS_WEB = Platform.OS === "web";
const WEB_TRANSITION: ViewStyle = IS_WEB
  ? ({
      transitionProperty: "background-color, border-color, color, opacity, transform, padding",
      transitionDuration: "200ms",
      transitionTimingFunction: "ease-out",
    } as ViewStyle)
  : {};

/* ────────────────────────── Static content ────────────────────────── */

const HERO_PROCESS_ITEMS: { num: string; title: string; desc: string }[] = [
  {
    num: "01",
    title: "We design the case",
    desc: "A custom brief built around a real challenge in your business.",
  },
  {
    num: "02",
    title: "We run the program",
    desc: "Venue, outreach, prize pool, student management — fully handled.",
  },
  {
    num: "03",
    title: "You watch live",
    desc: "Your client portal shows every candidate, team, and submission in real time.",
  },
  {
    num: "04",
    title: "You hire the best",
    desc: "Fast-track interviews and direct pipeline control — on your schedule.",
  },
];

type Phase = {
  tag: string;
  name: string;
  title: string;
  description: string;
  pill: string;
  // Optional mid-paragraph emphasis span — rendered at higher opacity inline.
  emphasis?: { before: string; bracket: string; after: string };
};

const PROCESS_PHASES: Phase[] = [
  {
    tag: "Phase 1",
    name: "Discovery",
    title: "An introductory call with your Coravu representative",
    description:
      "We begin with a focused virtual meeting to understand your recruiting goals, the talent profile you are targeting, any constraints on the case structure, and your desired timeline and budget. No pressure — just alignment.",
    pill: "Virtual · ~60 minutes",
  },
  {
    tag: "Phase 2",
    name: "Custom Case Design",
    title: "Coravu builds your competition — before any payment",
    description:
      "Based on your goals, we draft a complete competition brief: the project directive, required deliverables, timeline, presentation format, and judging criteria. Every detail a student needs to walk in fully prepared. You review everything before committing to anything.",
    pill: "No payment required at this stage",
  },
  {
    tag: "Phase 3",
    name: "Onboarding Call and Contract",
    title: "We walk through the program together — and sign on the call",
    description:
      "A Coravu representative presents the finalized competition structure, answers any remaining questions, and addresses any revisions. If you are satisfied, we execute the contract in that same session. No back-and-forth delays.",
    pill: "Virtual · Contract executed same call",
  },
  {
    tag: "Phase 4",
    name: "Launch Preparation",
    title: "Venue secured. Student outreach begins.",
    description:
      "Once the flat upfront program fee is received, Coravu locks in the venue and activates campus outreach — notifying both new and returning student competitors about your program. The pipeline starts filling before you lift a finger.",
    pill: "Triggered by flat program fee",
  },
  {
    tag: "Phase 5",
    name: "Work Period",
    title: "Students receive the brief and begin work",
    description:
      "After the pre-registration window closes, the case brief is released and the work period opens. The duration is calibrated to the volume and complexity of the deliverables agreed upon in the initial calls — enough time for serious work, tight enough to test execution under pressure.",
    pill: "Timeline set at contract stage",
  },
  {
    tag: "Phase 6",
    name: "Live Monitoring (Optional)",
    title: "Stay as close — or as hands-off — as you want",
    description:
      "Your client portal gives you a live window into the competition as it unfolds. Review submissions as they arrive, track team formation and progress, and reach out to guide or clarify as needed. Or simply let it run and review everything at close.",
    pill: "Via Client Portal · Direct student contact available",
    emphasis: {
      before: " ",
      bracket:
        "You can also contact any registered student directly from the platform",
      after:
        " — every competitor profile includes contact information accessible through your portal, so you can begin building relationships before the competition even closes.",
    },
  },
  {
    tag: "Phase 7",
    name: "Presentation Day",
    title: "Your team shows up. Your new hire is in that room.",
    description:
      "Your elected representatives attend in person to judge presentations and network directly with competitors. Your staff serves as the judges — evaluating the work firsthand and engaging with the talent you came to find. On request, Coravu organizes a private winner's circle event for your top-selected groups, a curated setting where recruiting is the only agenda.",
    pill: "In person · Winner's Circle available on request",
  },
];

const PRICING_TIERS: { num: string; title: string; desc: string }[] = [
  {
    num: "01",
    title: "Flat Program Fee",
    desc: "Venue, outreach, case design, prize pool, and full operations — one upfront fee.",
  },
  {
    num: "02",
    title: "Per-Student Fee",
    desc: "Scales with participation volume. You pay based on how many students compete.",
  },
  {
    num: "03",
    title: "Placement Percentage",
    desc: "A percentage of first-year compensation for any student you hire through the program.",
  },
];

const COMPANY_SIZE_OPTIONS = [
  "1-10 employees",
  "11-50 employees",
  "51-200 employees",
  "201-500 employees",
  "501-1000 employees",
  "1000+ employees",
];
const FORMAT_OPTIONS = ["In-person", "Virtual", "No preference — advise me"];

/* ────────────────────────── Form state ────────────────────────── */

type FormFields = {
  first_name: string;
  last_name: string;
  company: string;
  email: string;
  company_size: string;
  roles: string;
  preferred_format: string;
  notes: string;
};

type FormErrors = Partial<Record<keyof FormFields, string>>;

const INITIAL_FORM: FormFields = {
  first_name: "",
  last_name: "",
  company: "",
  email: "",
  company_size: "",
  roles: "",
  preferred_format: "",
  notes: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateForm(form: FormFields): FormErrors {
  const errs: FormErrors = {};
  if (!form.first_name.trim()) errs.first_name = "Required";
  if (!form.last_name.trim()) errs.last_name = "Required";
  if (!form.company.trim()) errs.company = "Required";
  if (!form.email.trim()) errs.email = "Required";
  else if (!EMAIL_RE.test(form.email.trim())) errs.email = "Enter a valid email";
  if (!form.company_size) errs.company_size = "Required";
  if (!form.roles.trim()) errs.roles = "Required";
  if (!form.preferred_format) errs.preferred_format = "Required";
  return errs;
}

/* ────────────────────────── Main screen ────────────────────────── */

export default function ClientOnboardingScreen() {
  const { width: winW } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const fontsReady = useMarketingFonts();

  const scrollRef = useRef<ScrollView>(null);
  const inquiryYRef = useRef(0);

  const isNarrow = winW < NARROW_BREAKPOINT;
  const pad = winW >= 900 ? 60 : winW >= 600 ? 36 : 22;

  const scrollToInquiry = () => {
    const y = Math.max(0, inquiryYRef.current - 8);
    scrollRef.current?.scrollTo({ y, animated: true });
  };

  if (!fontsReady) {
    if (Platform.OS === "web") {
      return (
        <View style={{ flex: 1, backgroundColor: BLACK }}>
          <StatusBar barStyle="light-content" />
        </View>
      );
    }
    return (
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center", backgroundColor: BLACK }}>
        <StatusBar barStyle="light-content" />
        <Text style={{ fontFamily: "DMSans_400Regular", color: "rgba(247,244,238,0.5)" }}>Loading…</Text>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: BLACK }}>
      <Head>
        <meta name="robots" content="noindex,nofollow" />
      </Head>
      <StatusBar barStyle="light-content" />

      <Nav
        winW={winW}
        topInset={insets.top}
        pad={pad}
        onLogo={() => router.push("/" as Href)}
        onBackHome={() => router.push("/" as Href)}
      />

      <ScrollView
        ref={scrollRef}
        style={{ flex: 1, backgroundColor: BLACK }}
        contentContainerStyle={{ paddingTop: NAV_HEIGHT + insets.top }}
        showsVerticalScrollIndicator={false}
      >
        <Hero
          winW={winW}
          pad={pad}
          isNarrow={isNarrow}
          onCta={scrollToInquiry}
          onClientPortal={() => router.push("/login" as Href)}
        />

        <StatBar pad={pad} isNarrow={isNarrow} />

        <ProcessMap pad={pad} isNarrow={isNarrow} />

        <DashboardMockup pad={pad} winW={winW} isNarrow={isNarrow} />

        <View
          onLayout={(e) => {
            inquiryYRef.current = e.nativeEvent.layout.y;
          }}
          // The inquiry anchor — hero CTA scrolls here.
          // The Inquiry component renders its own padded section below.
        >
          <Inquiry pad={pad} isNarrow={isNarrow} />
        </View>

        <MarketingFooter />
      </ScrollView>
    </View>
  );
}

/* ────────────────────────── Nav ────────────────────────── */

function Nav({
  winW,
  topInset,
  pad,
  onLogo,
  onBackHome,
}: {
  winW: number;
  topInset: number;
  pad: number;
  onLogo: () => void;
  onBackHome: () => void;
}) {
  const compact = winW < 600;
  return (
    <View
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 200,
        paddingTop: topInset,
        paddingHorizontal: pad,
        height: NAV_HEIGHT + topInset,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: "rgba(8,8,8,0.86)",
        borderBottomWidth: 1,
        borderBottomColor: "rgba(237,232,223,0.06)",
        ...(IS_WEB ? ({ backdropFilter: "blur(10px)" } as ViewStyle) : {}),
      }}
    >
      <Pressable
        onPress={onLogo}
        accessibilityRole="link"
        accessibilityLabel="Coravu home"
        style={({ hovered }) => [
          { flexDirection: "row", alignItems: "center", gap: 12 },
          IS_WEB && hovered ? { opacity: 0.85 } : null,
        ]}
      >
        <LogoMark size={30} ringOuter={SAGE} ringInner={AMBER} dot={AMBER} />
        <LogoWordmark fontSize={compact ? 18.4 : 20.4} letterSpacing={7.2} color={LINEN} accent={AMBER} />
      </Pressable>

      <View style={{ flexDirection: "row", alignItems: "center", gap: compact ? 14 : 28 }}>
        <NavLink label="Back to Home" onPress={onBackHome} accent compact={compact} />
      </View>
    </View>
  );
}

function NavLink({
  label,
  onPress,
  accent,
  compact,
}: {
  label: string;
  onPress: () => void;
  accent?: boolean;
  compact?: boolean;
}) {
  return (
    <Pressable onPress={onPress} accessibilityRole="link">
      {({ hovered, pressed }) => (
        <Text
          style={{
            fontFamily: "DMSans_600SemiBold",
            fontSize: compact ? 10.4 : 11.4,
            letterSpacing: 1.6,
            textTransform: "uppercase",
            color:
              pressed
                ? "rgba(237,232,223,0.6)"
                : IS_WEB && hovered
                ? accent
                  ? AMBER_WARM
                  : LINEN
                : accent
                ? AMBER
                : "rgba(237,232,223,0.55)",
            ...(IS_WEB
              ? ({
                  transitionProperty: "color",
                  transitionDuration: "180ms",
                } as TextStyle)
              : {}),
          }}
        >
          {label}
        </Text>
      )}
    </Pressable>
  );
}

/* ────────────────────────── Hero ────────────────────────── */

function Hero({
  winW,
  pad,
  isNarrow,
  onCta,
  onClientPortal,
}: {
  winW: number;
  pad: number;
  isNarrow: boolean;
  onCta: () => void;
  onClientPortal: () => void;
}) {
  const headlineSize = winW >= 1200 ? 90 : winW >= 900 ? 76 : winW >= 600 ? 64 : 54;

  const left = (
    <View
      style={{
        flex: isNarrow ? undefined : 1,
        paddingHorizontal: pad,
        paddingVertical: isNarrow ? 56 : 80,
        borderRightWidth: isNarrow ? 0 : 1,
        borderRightColor: "rgba(247,244,238,0.06)",
        justifyContent: "center",
      }}
    >
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 10.4,
          letterSpacing: 2.2,
          textTransform: "uppercase",
          color: AMBER,
          marginBottom: 28,
        }}
      >
        For Companies — Precision Talent
      </Text>

      <Text
        style={{
          fontFamily: "CormorantGaramond_300Light",
          fontSize: headlineSize,
          lineHeight: headlineSize * 0.97,
          letterSpacing: -0.02 * headlineSize,
          color: "rgba(247,244,238,0.35)",
          marginBottom: 0,
        }}
      >
        Stop guessing.
      </Text>
      <Text
        style={{
          fontFamily: "CormorantGaramond_300Light",
          fontStyle: "italic",
          fontSize: headlineSize,
          lineHeight: headlineSize * 0.97,
          letterSpacing: -0.02 * headlineSize,
          color: AMBER,
          marginBottom: 32,
        }}
      >
        Start knowing.
      </Text>

      <Text
        style={{
          fontFamily: "DMSans_300Light",
          fontSize: 16,
          lineHeight: 28,
          color: "rgba(247,244,238,0.5)",
          maxWidth: 460,
          marginBottom: 44,
        }}
      >
        Coravu runs end-to-end case competitions so you can{" "}
        <Text style={{ fontFamily: "DMSans_500Medium", color: LINEN }}>
          evaluate talent on live performance
        </Text>{" "}
        — not resumes. Watch candidates work your real business challenge, then
        hire the ones who impressed you.
      </Text>

      <SplitCtaButton onPress={onCta} />

      <Pressable
        onPress={onClientPortal}
        accessibilityRole="link"
        style={({ hovered }) => [
          { marginTop: 16, alignSelf: "flex-start" },
          IS_WEB && hovered ? { opacity: 0.95 } : null,
        ]}
      >
        {({ hovered }) => (
          <Text
            style={{
              fontFamily: "DMSans_400Regular",
              fontStyle: "italic",
              fontSize: 12,
              color: "rgba(247,244,238,0.28)",
            }}
          >
            Already a client?{" "}
            <Text
              style={{
                fontFamily: "DMSans_500Medium",
                fontStyle: "normal",
                color: IS_WEB && hovered ? AMBER_WARM : AMBER,
                textDecorationLine: "underline",
                textDecorationColor: "rgba(181,135,58,0.4)",
              }}
            >
              Access Client Portal →
            </Text>
          </Text>
        )}
      </Pressable>
    </View>
  );

  const right = (
    <View
      style={{
        flex: isNarrow ? undefined : 1,
        paddingHorizontal: pad,
        paddingVertical: isNarrow ? 24 : 80,
        justifyContent: "center",
      }}
    >
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 10,
          letterSpacing: 1.8,
          textTransform: "uppercase",
          color: "rgba(247,244,238,0.2)",
          marginBottom: 24,
        }}
      >
        How Coravu works
      </Text>

      {HERO_PROCESS_ITEMS.map((item, idx) => (
        <View
          key={item.num}
          style={{
            flexDirection: "row",
            gap: 20,
            paddingVertical: 20,
            borderBottomWidth: idx === HERO_PROCESS_ITEMS.length - 1 ? 0 : 1,
            borderBottomColor: "rgba(247,244,238,0.06)",
          }}
        >
          <Text
            style={{
              fontFamily: "CormorantGaramond_300Light",
              fontSize: 29,
              color: "rgba(181,135,58,0.25)",
              width: 36,
              flexShrink: 0,
              lineHeight: 32,
            }}
          >
            {item.num}
          </Text>
          <View style={{ flex: 1 }}>
            <Text
              style={{
                fontFamily: "DMSans_600SemiBold",
                fontSize: 13.6,
                color: LINEN,
                marginBottom: 4,
              }}
            >
              {item.title}
            </Text>
            <Text
              style={{
                fontFamily: "DMSans_300Light",
                fontSize: 12.5,
                lineHeight: 20.6,
                color: "rgba(247,244,238,0.38)",
              }}
            >
              {item.desc}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );

  return (
    <View style={{ backgroundColor: BLACK }}>
      <View
        style={{
          flexDirection: isNarrow ? "column" : "row",
          minHeight: isNarrow ? undefined : 580,
        }}
      >
        {left}
        {right}
      </View>
    </View>
  );
}

function SplitCtaButton({ onPress }: { onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel="Build Your Pipeline — scroll to inquiry form"
      style={({ hovered, pressed }) => [
        {
          flexDirection: "row",
          alignSelf: "flex-start",
          borderRadius: 2,
          overflow: "hidden",
        },
        pressed ? { opacity: 0.92 } : null,
        IS_WEB && hovered ? { transform: [{ translateY: -1 }] } : null,
      ]}
    >
      {({ hovered }) => {
        const isHover = IS_WEB && Boolean(hovered);
        return (
          <>
            <View
              style={{
                backgroundColor: isHover ? AMBER_WARM : AMBER,
                paddingVertical: 18,
                paddingHorizontal: 28,
                ...WEB_TRANSITION,
              }}
            >
              <Text
                style={{
                  fontFamily: "DMSans_700Bold",
                  fontSize: 10,
                  letterSpacing: 1.6,
                  textTransform: "uppercase",
                  color: BLACK,
                  opacity: 0.6,
                  marginBottom: 3,
                }}
              >
                No commitment required
              </Text>
              <Text
                style={{
                  fontFamily: "CormorantGaramond_600SemiBold",
                  fontSize: 19,
                  color: BLACK,
                }}
              >
                Build Your Pipeline
              </Text>
            </View>
            <View
              style={{
                backgroundColor: isHover ? "#333" : CHARCOAL,
                paddingVertical: 18,
                paddingLeft: 20,
                paddingRight: isHover ? 26 : 20,
                justifyContent: "center",
                ...WEB_TRANSITION,
              }}
            >
              <Text style={{ fontSize: 20, color: AMBER, lineHeight: 22 }}>→</Text>
            </View>
          </>
        );
      }}
    </Pressable>
  );
}

/* ────────────────────────── Stat bar ────────────────────────── */

function StatBar({ pad, isNarrow }: { pad: number; isNarrow: boolean }) {
  const stats: { value: string; suffix: string; label: string }[] = [
    { value: "0", suffix: "x", label: "Internal lift required" },
    { value: "100", suffix: "%", label: "Managed by Coravu" },
    { value: "1", suffix: "st", label: "See work before the interview" },
  ];

  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: isNarrow ? "wrap" : "nowrap",
        borderTopWidth: 1,
        borderTopColor: "rgba(247,244,238,0.06)",
        backgroundColor: BLACK,
        paddingHorizontal: isNarrow ? pad / 2 : 0,
      }}
    >
      {stats.map((s, idx) => (
        <View
          key={s.label}
          style={{
            flex: isNarrow ? undefined : 1,
            width: isNarrow ? "100%" : undefined,
            paddingVertical: 28,
            paddingHorizontal: 36,
            borderRightWidth: !isNarrow && idx < stats.length - 1 ? 1 : 0,
            borderRightColor: "rgba(247,244,238,0.06)",
            borderBottomWidth: isNarrow && idx < stats.length - 1 ? 1 : 0,
            borderBottomColor: "rgba(247,244,238,0.06)",
          }}
        >
          <Text style={{ fontFamily: "CormorantGaramond_300Light", fontSize: 32, lineHeight: 36, color: LINEN }}>
            {s.value}
            <Text style={{ color: AMBER }}>{s.suffix}</Text>
          </Text>
          <Text
            style={{
              fontFamily: "DMSans_400Regular",
              fontSize: 11.2,
              letterSpacing: 0.6,
              color: "rgba(247,244,238,0.28)",
              marginTop: 6,
            }}
          >
            {s.label}
          </Text>
        </View>
      ))}
    </View>
  );
}

/* ────────────────────────── Process map ────────────────────────── */

function ProcessMap({ pad, isNarrow }: { pad: number; isNarrow: boolean }) {
  const headlineSize = isNarrow ? 36 : 52;
  return (
    <View
      style={{
        backgroundColor: FOREST_DEEP,
        borderTopWidth: 1,
        borderTopColor: "rgba(247,244,238,0.06)",
        paddingHorizontal: pad,
        paddingVertical: isNarrow ? 64 : 100,
      }}
    >
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 10.4,
          letterSpacing: 2.2,
          textTransform: "uppercase",
          color: AMBER,
          marginBottom: 18,
        }}
      >
        The Coravu Process
      </Text>

      <Text
        style={{
          fontFamily: "CormorantGaramond_300Light",
          fontSize: headlineSize,
          lineHeight: headlineSize * 1.05,
          color: LINEN,
        }}
      >
        From first call
      </Text>
      <Text
        style={{
          fontFamily: "CormorantGaramond_300Light",
          fontSize: headlineSize,
          lineHeight: headlineSize * 1.05,
          color: LINEN,
        }}
      >
        to{" "}
        <Text style={{ fontStyle: "italic", color: AMBER }}>hired talent.</Text>
      </Text>

      <Text
        style={{
          fontFamily: "DMSans_300Light",
          fontSize: 14.4,
          lineHeight: 25.6,
          color: "rgba(247,244,238,0.38)",
          maxWidth: 580,
          marginTop: 24,
          marginBottom: 64,
        }}
      >
        Every engagement follows a defined path — transparent, collaborative, and
        built to move at your pace. Here is exactly what to expect from the moment
        we connect.
      </Text>

      <View style={{ position: "relative" }}>
        {/* Vertical guideline */}
        <View
          style={{
            position: "absolute",
            left: 28,
            top: 28,
            bottom: 28,
            width: 1,
            backgroundColor: "rgba(181,135,58,0.18)",
          }}
        />

        {PROCESS_PHASES.map((phase, idx) => (
          <PhaseNode key={phase.tag} phase={phase} isLast={idx === PROCESS_PHASES.length - 1} />
        ))}
      </View>
    </View>
  );
}

function PhaseNode({ phase, isLast }: { phase: Phase; isLast: boolean }) {
  return (
    <View
      style={{
        flexDirection: "row",
        marginBottom: isLast ? 0 : 16,
        alignItems: "stretch",
      }}
    >
      <View style={{ width: 56, alignItems: "center", paddingTop: 26 }}>
        <View
          style={{
            width: 14,
            height: 14,
            borderRadius: 7,
            borderWidth: 2,
            borderColor: AMBER,
            backgroundColor: FOREST_DEEP,
          }}
        />
      </View>

      <View
        style={{
          flex: 1,
          marginLeft: 24,
          padding: 24,
          paddingHorizontal: 28,
          backgroundColor: "rgba(247,244,238,0.03)",
          borderWidth: 1,
          borderColor: "rgba(247,244,238,0.06)",
          borderLeftWidth: 2,
          borderLeftColor: "rgba(181,135,58,0.3)",
          borderRadius: 4,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
        }}
      >
        <Text
          style={{
            fontFamily: "DMSans_700Bold",
            fontSize: 9.3,
            letterSpacing: 1.8,
            textTransform: "uppercase",
            color: AMBER,
            marginBottom: 10,
          }}
        >
          {phase.tag} · {phase.name}
        </Text>
        <Text
          style={{
            fontFamily: "CormorantGaramond_500Medium",
            fontSize: 19.2,
            lineHeight: 26,
            color: LINEN,
            marginBottom: 10,
          }}
        >
          {phase.title}
        </Text>
        <Text
          style={{
            fontFamily: "DMSans_300Light",
            fontSize: 12.8,
            lineHeight: 21.8,
            color: "rgba(247,244,238,0.42)",
          }}
        >
          {phase.description}
          {phase.emphasis ? (
            <>
              {phase.emphasis.before}
              <Text style={{ color: "rgba(247,244,238,0.75)" }}>{phase.emphasis.bracket}</Text>
              {phase.emphasis.after}
            </>
          ) : null}
        </Text>
        <View
          style={{
            alignSelf: "flex-start",
            marginTop: 10,
            paddingVertical: 4,
            paddingHorizontal: 10,
            borderRadius: 20,
            backgroundColor: "rgba(181,135,58,0.12)",
            borderWidth: 1,
            borderColor: "rgba(181,135,58,0.25)",
          }}
        >
          <Text
            style={{
              fontFamily: "DMSans_700Bold",
              fontSize: 9.3,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            {phase.pill}
          </Text>
        </View>
      </View>
    </View>
  );
}

/* ────────────────────────── Dashboard mockup ────────────────────────── */

function DashboardMockup({ pad, winW, isNarrow }: { pad: number; winW: number; isNarrow: boolean }) {
  const headlineSize = isNarrow ? 38 : winW >= 1100 ? 56 : 48;
  const hideSidebar = winW < 1000;

  return (
    <View style={{ backgroundColor: SAND, paddingHorizontal: pad, paddingVertical: isNarrow ? 64 : 90 }}>
      <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 18 }}>
        <View style={{ width: 24, height: 1, backgroundColor: AMBER, marginRight: 12 }} />
        <Text
          style={{
            fontFamily: "DMSans_700Bold",
            fontSize: 10.4,
            letterSpacing: 2.2,
            textTransform: "uppercase",
            color: AMBER,
          }}
        >
          The Client Portal
        </Text>
      </View>

      <Text
        style={{
          fontFamily: "CormorantGaramond_400Regular",
          fontSize: headlineSize,
          lineHeight: headlineSize * 1.05,
          color: BLACK,
        }}
      >
        Your program.
      </Text>
      <Text
        style={{
          fontFamily: "CormorantGaramond_400Regular",
          fontSize: headlineSize,
          lineHeight: headlineSize * 1.05,
          fontStyle: "italic",
          color: AMBER,
        }}
      >
        Fully visible.
      </Text>

      <Text
        style={{
          fontFamily: "DMSans_300Light",
          fontSize: 15.2,
          lineHeight: 28,
          color: "rgba(8,8,8,0.55)",
          maxWidth: 640,
          marginTop: 24,
          marginBottom: 52,
        }}
      >
        The moment your competition goes live, your client portal becomes your
        command center. No emails, no spreadsheets — every candidate, team, and
        deliverable in one private dashboard.
      </Text>

      <View
        style={{
          backgroundColor: FOREST_DEEP,
          borderWidth: 1,
          borderColor: "rgba(247,244,238,0.07)",
          borderRadius: 8,
          overflow: "hidden",
          ...(IS_WEB ? ({ boxShadow: "0 24px 80px rgba(0,0,0,0.2)" } as ViewStyle) : {}),
        }}
      >
        <DashboardTopBar />
        <View style={{ flexDirection: "row" }}>
          {hideSidebar ? null : <DashboardSidebar />}
          <DashboardMainPane compact={hideSidebar} />
        </View>
      </View>
    </View>
  );
}

function DashboardTopBar() {
  // Mirrors the real OverviewShell topbar: page title (case name) on the
  // left + initials circle on the right. No tabs — navigation lives in the
  // sidebar.
  return (
    <View
      style={{
        backgroundColor: "rgba(14,14,14,0.97)",
        borderBottomWidth: 1,
        borderBottomColor: "rgba(247,244,238,0.06)",
        paddingHorizontal: 20,
        height: 46,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Text
        style={{
          fontFamily: "CormorantGaramond_500Medium",
          fontSize: 15.5,
          color: LINEN,
          flexShrink: 1,
        }}
        numberOfLines={1}
      >
        Market Entry Case
      </Text>

      <View
        style={{
          width: 26,
          height: 26,
          borderRadius: 13,
          backgroundColor: "rgba(36,53,33,0.9)",
          borderWidth: 1,
          borderColor: "rgba(181,135,58,0.30)",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text style={{ fontFamily: "CormorantGaramond_600SemiBold", fontSize: 11.4, color: AMBER }}>
          JS
        </Text>
      </View>
    </View>
  );
}

function DashboardSidebar() {
  // Mirrors the real OverviewShell sidebar: a header strip with the Coravu
  // mark + OVERVIEW eyebrow, then five always-visible overview items, then
  // an ACTIVE PROGRAM section with the case-scoped nav (active row =
  // Dashboard, since this mockup is showing the program dashboard view).
  return (
    <View
      style={{
        width: 200,
        flexShrink: 0,
        backgroundColor: "#1a2418",
        borderRightWidth: 1,
        borderRightColor: "rgba(181,135,58,0.12)",
        flexDirection: "column",
      }}
    >
      {/* Logo + portal eyebrow */}
      <View
        style={{
          height: 46,
          paddingHorizontal: 16,
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
          borderBottomWidth: 1,
          borderBottomColor: "rgba(181,135,58,0.12)",
        }}
      >
        <LogoMark size={20} ringOuter={SAGE} ringInner={AMBER} dot={AMBER} rays="none" />
        <View style={{ minWidth: 0, flexShrink: 1 }}>
          <LogoWordmark fontSize={13.2} letterSpacing={2.6} color={LINEN} accent={AMBER} />
          <Text
            style={{
              fontFamily: "DMSans_700Bold",
              fontSize: 7.2,
              letterSpacing: 1.8,
              textTransform: "uppercase",
              color: "rgba(237,232,223,0.28)",
              marginTop: 1,
            }}
          >
            Overview
          </Text>
        </View>
      </View>

      <View style={{ paddingVertical: 10 }}>
        <SidebarNavRow label="Dashboard" />
        <SidebarNavRow label="Candidates" />
        <SidebarNavRow label="Your Company" />
        <SidebarNavRow label="Team" />
        <SidebarNavRow label="Settings" />

        {/* Active-program divider */}
        <View
          style={{
            height: 1,
            backgroundColor: "rgba(255,255,255,0.06)",
            marginVertical: 12,
            marginHorizontal: 16,
          }}
        />

        <Text
          style={{
            fontFamily: "DMSans_700Bold",
            fontSize: 7.8,
            letterSpacing: 2.2,
            textTransform: "uppercase",
            color: "rgba(237,232,223,0.30)",
            paddingHorizontal: 16,
            marginBottom: 4,
          }}
        >
          Active Program
        </Text>
        <Text
          style={{
            fontFamily: "DMSans_500Medium",
            fontSize: 11,
            lineHeight: 14.5,
            color: "rgba(237,232,223,0.60)",
            paddingHorizontal: 16,
            marginBottom: 6,
          }}
          numberOfLines={2}
        >
          Market Entry Case
        </Text>
        <View style={{ paddingHorizontal: 16, marginBottom: 8 }}>
          <View
            style={{
              alignSelf: "flex-start",
              paddingVertical: 2,
              paddingHorizontal: 7,
              borderRadius: 10,
              backgroundColor: "rgba(127,203,122,0.12)",
              borderWidth: 1,
              borderColor: "rgba(127,203,122,0.28)",
            }}
          >
            <Text
              style={{
                fontFamily: "DMSans_700Bold",
                fontSize: 7.4,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: LIVE_GREEN,
              }}
            >
              Program Active
            </Text>
          </View>
        </View>

        <SidebarNavRow label="Dashboard" active />
        <SidebarNavRow label="Edit case" />
        <SidebarNavRow label="Candidates" />
        <SidebarNavRow label="Deliverables" />
        <SidebarNavRow label="Positions" />
        <SidebarNavRow label="Pipeline" />
        <SidebarNavRow label="Shortlist" />
      </View>

      {/* Sidebar footer: company + role + sign out, matching the real shell. */}
      <View
        style={{
          marginTop: "auto",
          paddingHorizontal: 16,
          paddingVertical: 12,
          borderTopWidth: 1,
          borderTopColor: "rgba(181,135,58,0.12)",
        }}
      >
        <Text
          style={{ fontFamily: "DMSans_600SemiBold", fontSize: 10.5, color: LINEN }}
          numberOfLines={1}
        >
          Acme Capital Partners
        </Text>
        <Text
          style={{
            fontFamily: "DMSans_400Regular",
            fontSize: 9,
            color: "rgba(237,232,223,0.30)",
            marginTop: 1,
          }}
          numberOfLines={1}
        >
          Jane Smith · Recruiting Lead
        </Text>
        <Text
          style={{
            fontFamily: "DMSans_500Medium",
            fontSize: 9.4,
            color: "rgba(237,232,223,0.30)",
            marginTop: 6,
          }}
        >
          Sign out
        </Text>
      </View>
    </View>
  );
}

function SidebarNavRow({ label, active }: { label: string; active?: boolean }) {
  return (
    <View
      style={{
        paddingHorizontal: 16,
        paddingVertical: 7,
        flexDirection: "row",
        alignItems: "center",
        gap: 9,
        backgroundColor: active ? "rgba(181,135,58,0.10)" : "transparent",
        borderLeftWidth: 2,
        borderLeftColor: active ? AMBER : "transparent",
      }}
    >
      <View
        style={{
          width: 5,
          height: 5,
          borderRadius: 2.5,
          backgroundColor: active ? AMBER : "rgba(247,244,238,0.18)",
        }}
      />
      <Text
        style={{
          fontFamily: active ? "DMSans_600SemiBold" : "DMSans_400Regular",
          fontSize: 10.6,
          color: active ? LINEN : "rgba(247,244,238,0.55)",
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function DashboardMainPane({ compact }: { compact: boolean }) {
  // Stat labels and order mirror the real client dashboard
  // (mobile/app/(client)/client/competition/[id]/dashboard.tsx StatTile row).
  const stats: { value: string; label: string; delta: string | null; amber?: boolean }[] = [
    { value: "48", label: "Registered Students", delta: null },
    { value: "12", label: "Teams Formed", delta: null },
    { value: "31", label: "Deliverables Submitted", delta: null },
    { value: "8", label: "Days Remaining", delta: "Deadline approaching", amber: true },
  ];

  return (
    <View style={{ flex: 1, padding: 20 }}>
      {/* Page header strip — matches the real dashboard's CLIENT PORTAL eyebrow
          + Cormorant case-name H1 + status subtitle. */}
      <View style={{ marginBottom: 18 }}>
        <Text
          style={{
            fontFamily: "DMSans_700Bold",
            fontSize: 8.4,
            letterSpacing: 2,
            textTransform: "uppercase",
            color: AMBER,
            marginBottom: 4,
          }}
        >
          Client Portal
        </Text>
        <Text
          style={{
            fontFamily: "CormorantGaramond_300Light",
            fontSize: 24,
            lineHeight: 26,
            color: LINEN,
          }}
          numberOfLines={1}
        >
          Market Entry Case
        </Text>
        <Text
          style={{
            fontFamily: "DMSans_300Light",
            fontSize: 11,
            color: "rgba(247,244,238,0.55)",
            marginTop: 4,
          }}
          numberOfLines={1}
        >
          Acme Capital Partners · Program active · 8 days remaining
        </Text>
      </View>

      {/* Stat row — same labels and "amber delta on Days Remaining" pattern
          as the real dashboard. */}
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: 18,
        }}
      >
        {stats.map((s) => (
          <View
            key={s.label}
            style={{
              flexGrow: 1,
              flexBasis: compact ? "47%" : "22%",
              backgroundColor: "rgba(247,244,238,0.025)",
              borderWidth: 1,
              borderColor: "rgba(247,244,238,0.06)",
              borderRadius: 4,
              paddingVertical: 13,
              paddingHorizontal: 15,
            }}
          >
            <Text
              style={{
                fontFamily: "CormorantGaramond_300Light",
                fontSize: 30,
                lineHeight: 32,
                color: s.amber ? AMBER : LINEN,
              }}
            >
              {s.value}
            </Text>
            <Text
              style={{
                fontFamily: "DMSans_400Regular",
                fontSize: 9.4,
                letterSpacing: 0.4,
                color: "rgba(247,244,238,0.3)",
                marginTop: 4,
              }}
            >
              {s.label}
            </Text>
            {s.delta ? (
              <Text
                style={{
                  fontFamily: "DMSans_600SemiBold",
                  fontSize: 8.8,
                  marginTop: 5,
                  color: s.amber ? AMBER : LIVE_GREEN,
                }}
              >
                {s.delta}
              </Text>
            ) : null}
          </View>
        ))}
      </View>

      {/* Top Candidates card — eyebrow + View All pill header, then rows
          with avatar / name / "major · university · team" meta / status badge.
          Matches the DashCard + CandidateRow shape in the real dashboard. */}
      <View
        style={{
          backgroundColor: "rgba(247,244,238,0.025)",
          borderWidth: 1,
          borderColor: "rgba(247,244,238,0.06)",
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        <View
          style={{
            paddingHorizontal: 16,
            paddingVertical: 10,
            borderBottomWidth: 1,
            borderBottomColor: "rgba(247,244,238,0.06)",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Text
            style={{
              fontFamily: "DMSans_700Bold",
              fontSize: 9,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: "rgba(247,244,238,0.28)",
            }}
          >
            Top Candidates — Live Feed
          </Text>
          <View
            style={{
              paddingVertical: 4,
              paddingHorizontal: 9,
              backgroundColor: "rgba(247,244,238,0.06)",
              borderWidth: 1,
              borderColor: "rgba(247,244,238,0.10)",
              borderRadius: 2,
            }}
          >
            <Text
              style={{
                fontFamily: "DMSans_700Bold",
                fontSize: 8.2,
                letterSpacing: 1.3,
                textTransform: "uppercase",
                color: LINEN,
              }}
            >
              View All
            </Text>
          </View>
        </View>

        <CandidateRow
          initials="AJ"
          name="Alex Johnson"
          meta={["Finance", "Wharton", "Team Alpha"]}
          status="submitted"
        />
        <CandidateRow
          initials="SR"
          name="Sofia Reyes"
          meta={["Strategy", "Stanford GSB", "Team Bravo"]}
          status="submitted"
        />
        <CandidateRow
          initials="MP"
          name="Marcus Park"
          meta={["Operations", "MIT Sloan", "Solo"]}
          status="in_progress"
          last
        />
      </View>
    </View>
  );
}

function CandidateRow({
  initials,
  name,
  meta,
  status,
  last,
}: {
  initials: string;
  name: string;
  meta: string[];
  status: "submitted" | "in_progress";
  last?: boolean;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 11,
        paddingHorizontal: 16,
        paddingVertical: 11,
        borderBottomWidth: last ? 0 : 1,
        borderBottomColor: "rgba(247,244,238,0.04)",
      }}
    >
      <View
        style={{
          width: 28,
          height: 28,
          borderRadius: 14,
          backgroundColor: "rgba(181,135,58,0.12)",
          borderWidth: 1,
          borderColor: "rgba(181,135,58,0.22)",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text style={{ fontFamily: "CormorantGaramond_600SemiBold", fontSize: 11.5, color: AMBER }}>
          {initials}
        </Text>
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{ fontFamily: "DMSans_500Medium", fontSize: 12.4, color: LINEN }}
          numberOfLines={1}
        >
          {name}
        </Text>
        <Text
          style={{
            fontFamily: "DMSans_400Regular",
            fontSize: 10,
            color: "rgba(247,244,238,0.42)",
            marginTop: 2,
          }}
          numberOfLines={1}
        >
          {meta.join(" · ")}
        </Text>
      </View>
      <StatusBadge kind={status} />
    </View>
  );
}

function StatusBadge({ kind }: { kind: "submitted" | "in_progress" }) {
  const submitted = kind === "submitted";
  return (
    <View
      style={{
        paddingVertical: 3,
        paddingHorizontal: 8,
        borderRadius: 20,
        backgroundColor: submitted ? "rgba(127,203,122,0.10)" : "rgba(181,135,58,0.10)",
        borderWidth: 1,
        borderColor: submitted ? "rgba(127,203,122,0.22)" : "rgba(181,135,58,0.22)",
      }}
    >
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 8.4,
          letterSpacing: 1,
          textTransform: "uppercase",
          color: submitted ? LIVE_GREEN : AMBER,
        }}
      >
        {submitted ? "Submitted" : "In Progress"}
      </Text>
    </View>
  );
}

/* ────────────────────────── Inquiry ────────────────────────── */

function Inquiry({ pad, isNarrow }: { pad: number; isNarrow: boolean }) {
  return (
    <View
      style={{
        backgroundColor: BLACK,
        borderTopWidth: 1,
        borderTopColor: "rgba(247,244,238,0.06)",
        paddingHorizontal: pad,
        paddingVertical: isNarrow ? 64 : 90,
      }}
    >
      <View style={{ flexDirection: isNarrow ? "column" : "row", gap: isNarrow ? 48 : 64 }}>
        <InquiryLeft isNarrow={isNarrow} />
        <View style={{ flex: 1 }}>
          <InquiryForm />
        </View>
      </View>
    </View>
  );
}

function InquiryLeft({ isNarrow }: { isNarrow: boolean }) {
  const headlineSize = isNarrow ? 34 : 50;
  return (
    <View style={{ flex: 1 }}>
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 10.4,
          letterSpacing: 2.2,
          textTransform: "uppercase",
          color: AMBER,
          marginBottom: 18,
        }}
      >
        Get Started
      </Text>
      <Text
        style={{
          fontFamily: "CormorantGaramond_300Light",
          fontSize: headlineSize,
          lineHeight: headlineSize * 1.05,
          color: LINEN,
        }}
      >
        Let's build
      </Text>
      <Text
        style={{
          fontFamily: "CormorantGaramond_300Light",
          fontSize: headlineSize,
          lineHeight: headlineSize * 1.05,
          color: LINEN,
        }}
      >
        your <Text style={{ fontStyle: "italic", color: AMBER }}>pipeline.</Text>
      </Text>

      <Text
        style={{
          fontFamily: "DMSans_300Light",
          fontSize: 14.9,
          lineHeight: 27.5,
          color: "rgba(247,244,238,0.4)",
          marginTop: 24,
          marginBottom: 32,
        }}
      >
        Tell us about your company and what you are hiring for. We will follow up
        within 1 to 2 business days. No commitment required.
      </Text>

      {PRICING_TIERS.map((tier, idx) => (
        <View
          key={tier.num}
          style={{
            flexDirection: "row",
            gap: 14,
            paddingVertical: 18,
            borderBottomWidth: idx === PRICING_TIERS.length - 1 ? 0 : 1,
            borderBottomColor: "rgba(247,244,238,0.06)",
          }}
        >
          <Text
            style={{
              fontFamily: "CormorantGaramond_600SemiBold",
              fontSize: 17.6,
              color: AMBER,
              width: 24,
            }}
          >
            {tier.num}
          </Text>
          <View style={{ flex: 1 }}>
            <Text
              style={{
                fontFamily: "DMSans_600SemiBold",
                fontSize: 13.3,
                color: LINEN,
                marginBottom: 4,
              }}
            >
              — {tier.title}
            </Text>
            <Text
              style={{
                fontFamily: "DMSans_300Light",
                fontSize: 12.3,
                lineHeight: 19.7,
                color: "rgba(247,244,238,0.32)",
              }}
            >
              {tier.desc}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
}

function InquiryForm() {
  const [form, setForm] = useState<FormFields>(INITIAL_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const setField = <K extends keyof FormFields>(key: K, value: FormFields[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const onSubmit = async () => {
    if (submitting) return;
    setSubmitError(null);
    const errs = validateForm(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        company: form.company.trim(),
        email: form.email.trim().toLowerCase(),
        company_size: form.company_size,
        roles: form.roles.trim(),
        preferred_format: form.preferred_format,
        notes: form.notes.trim() || null,
      };

      // Submit via the SECURITY DEFINER RPC. A direct
      // .from("client_inquiries").insert(payload).select("id") fails for a
      // logged-out (anon) visitor: the .select() RETURNING is evaluated against
      // the table's SELECT policies, which anon can't satisfy — so every
      // submission errored. The RPC inserts + returns the id without exposing
      // the table. (See migration 20260603230002_client_inquiries_anon_submit_rpc.)
      const { data: newId, error: insertErr } = await supabase.rpc("submit_client_inquiry", {
        p_first_name: payload.first_name,
        p_last_name: payload.last_name,
        p_company: payload.company,
        p_email: payload.email,
        p_company_size: payload.company_size,
        p_roles: payload.roles,
        p_preferred_format: payload.preferred_format,
        p_notes: payload.notes,
      });

      if (insertErr) {
        console.error("client_inquiries submit failed", insertErr);
        setSubmitError("We couldn't save your inquiry. Please try again or email Client@coravu.com.");
        setSubmitting(false);
        return;
      }

      const inquiryId = typeof newId === "string" ? newId : undefined;

      // Fire the email notification. Don't block the success state on it —
      // the row is saved; Coravu staff can follow up manually if Resend
      // hiccups. We log + swallow errors here.
      void supabase.functions
        .invoke("notify-client-inquiry", {
          body: {
            inquiry_id: inquiryId,
            first_name: payload.first_name,
            last_name: payload.last_name,
            company: payload.company,
            email: payload.email,
            company_size: payload.company_size,
            roles: payload.roles,
            preferred_format: payload.preferred_format,
            notes: payload.notes ?? undefined,
          },
        })
        .catch((e: unknown) => {
          console.warn("[onboard/client] notify-client-inquiry invoke failed:", e);
        });

      void import("../../lib/analytics").then(({ track }) =>
        track("inquiry_submitted", {
          channel: "client_onboarding",
          company_size: payload.company_size,
          preferred_format: payload.preferred_format,
        })
      );

      setSubmitted(true);
    } catch (e) {
      console.error("client_inquiries submit threw", e);
      setSubmitError("Something went wrong. Please try again or email Client@coravu.com.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <SuccessCard />;
  }

  return (
    <View
      style={{
        backgroundColor: FOREST_DEEP,
        borderWidth: 1,
        borderColor: "rgba(247,244,238,0.07)",
        borderRadius: 4,
        padding: 40,
        gap: 13,
      }}
    >
      <Text style={{ fontFamily: "CormorantGaramond_300Light", fontSize: 25.6, color: LINEN }}>
        Tell us about your company
      </Text>
      <Text
        style={{
          fontFamily: "DMSans_300Light",
          fontSize: 12.8,
          color: "rgba(247,244,238,0.28)",
          marginBottom: 6,
        }}
      >
        No commitment. We respond within 1 to 2 business days.
      </Text>

      <View style={{ flexDirection: "row", gap: 12, flexWrap: "wrap" }}>
        <View style={{ flex: 1, minWidth: 180 }}>
          <Field
            label="First Name"
            value={form.first_name}
            onChange={(v) => setField("first_name", v)}
            error={errors.first_name}
          />
        </View>
        <View style={{ flex: 1, minWidth: 180 }}>
          <Field
            label="Last Name"
            value={form.last_name}
            onChange={(v) => setField("last_name", v)}
            error={errors.last_name}
          />
        </View>
      </View>

      <Field
        label="Company"
        value={form.company}
        onChange={(v) => setField("company", v)}
        error={errors.company}
      />

      <Field
        label="Work Email"
        value={form.email}
        onChange={(v) => setField("email", v)}
        error={errors.email}
        keyboardType="email-address"
        autoCapitalize="none"
      />

      <DropdownField
        label="Company Size"
        value={form.company_size}
        onChange={(v) => setField("company_size", v)}
        options={COMPANY_SIZE_OPTIONS}
        placeholder="Select size"
        error={errors.company_size}
      />

      <Field
        label="Roles You Are Looking to Fill"
        value={form.roles}
        onChange={(v) => setField("roles", v)}
        error={errors.roles}
      />

      <DropdownField
        label="Preferred Format"
        value={form.preferred_format}
        onChange={(v) => setField("preferred_format", v)}
        options={FORMAT_OPTIONS}
        placeholder="Choose a format"
        error={errors.preferred_format}
      />

      <Field
        label="Anything else?"
        value={form.notes}
        onChange={(v) => setField("notes", v)}
        multiline
      />

      {submitError ? (
        <Text
          style={{
            fontFamily: "DMSans_500Medium",
            fontSize: 12,
            color: COLORS.RED_ERROR.hex,
            marginTop: 4,
          }}
        >
          {submitError}
        </Text>
      ) : null}

      <Pressable
        onPress={onSubmit}
        accessibilityRole="button"
        disabled={submitting}
        style={({ hovered, pressed }) => {
          const isHover = IS_WEB && Boolean(hovered);
          return [
            {
              backgroundColor: submitting ? "rgba(181,135,58,0.55)" : isHover ? AMBER_WARM : AMBER,
              paddingVertical: 15,
              paddingHorizontal: 20,
              borderRadius: 2,
              alignItems: "center",
              marginTop: 8,
              ...WEB_TRANSITION,
            } as ViewStyle,
            pressed ? { opacity: 0.92 } : null,
          ];
        }}
      >
        <Text
          style={{
            fontFamily: "DMSans_700Bold",
            fontSize: 12.5,
            letterSpacing: 1.7,
            textTransform: "uppercase",
            color: BLACK,
          }}
        >
          {submitting ? "Sending…" : "Send Inquiry →"}
        </Text>
      </Pressable>

      <Text
        style={{
          fontFamily: "DMSans_400Regular",
          fontStyle: "italic",
          fontSize: 10.9,
          color: "rgba(247,244,238,0.2)",
          marginTop: 6,
        }}
      >
        Or email us directly at{" "}
        <Text
          accessibilityRole="link"
          onPress={() => {
            const url = "mailto:Client@coravu.com";
            if (Platform.OS === "web" && typeof window !== "undefined") {
              window.location.href = url;
            } else {
              void Linking.openURL(url);
            }
          }}
          style={{ color: AMBER, fontStyle: "normal" }}
        >
          Client@coravu.com
        </Text>
      </Text>
    </View>
  );
}

function SuccessCard() {
  return (
    <View
      style={{
        backgroundColor: FOREST_DEEP,
        borderWidth: 1,
        borderColor: "rgba(181,135,58,0.35)",
        borderRadius: 4,
        padding: 40,
        gap: 14,
        alignItems: "flex-start",
      }}
    >
      <View
        style={{
          paddingVertical: 4,
          paddingHorizontal: 12,
          borderRadius: 14,
          backgroundColor: "rgba(127,203,122,0.12)",
          borderWidth: 1,
          borderColor: "rgba(127,203,122,0.32)",
        }}
      >
        <Text
          style={{
            fontFamily: "DMSans_700Bold",
            fontSize: 9.6,
            letterSpacing: 1.8,
            textTransform: "uppercase",
            color: LIVE_GREEN,
          }}
        >
          Inquiry Received
        </Text>
      </View>
      <Text style={{ fontFamily: "CormorantGaramond_300Light", fontSize: 30, color: LINEN }}>
        Thanks — we'll be in touch.
      </Text>
      <Text
        style={{
          fontFamily: "DMSans_300Light",
          fontSize: 14.5,
          lineHeight: 26,
          color: "rgba(247,244,238,0.55)",
        }}
      >
        We'll review your inquiry and follow up within 1 to 2 business days. A
        confirmation email is on its way to the address you provided.
      </Text>
      <Text
        style={{
          fontFamily: "DMSans_400Regular",
          fontSize: 12.5,
          color: "rgba(247,244,238,0.32)",
          marginTop: 4,
        }}
      >
        Need to add something? Reply to the confirmation email or write to{" "}
        <Text style={{ color: AMBER, fontFamily: "DMSans_500Medium" }}>Client@coravu.com</Text>.
      </Text>
    </View>
  );
}

/* ────────────────────────── Field primitives ────────────────────────── */

function FieldLabel({ children }: { children: string }) {
  return (
    <Text
      style={{
        fontFamily: "DMSans_700Bold",
        fontSize: 9.9,
        letterSpacing: 1.3,
        textTransform: "uppercase",
        color: "rgba(247,244,238,0.25)",
        marginBottom: 5,
      }}
    >
      {children}
    </Text>
  );
}

function Field({
  label,
  value,
  onChange,
  error,
  keyboardType,
  autoCapitalize,
  multiline,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  keyboardType?: "default" | "email-address";
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  multiline?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const borderColor = error ? AMBER : focused ? AMBER : "rgba(247,244,238,0.08)";
  const bgColor = focused ? "rgba(247,244,238,0.06)" : "rgba(247,244,238,0.04)";

  return (
    <View>
      <FieldLabel>{label}</FieldLabel>
      <TextInput
        value={value}
        onChangeText={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        keyboardType={keyboardType ?? "default"}
        autoCapitalize={autoCapitalize}
        multiline={multiline}
        placeholderTextColor="rgba(247,244,238,0.15)"
        style={[
          {
            backgroundColor: bgColor,
            borderWidth: 1,
            borderColor,
            borderRadius: 2,
            paddingVertical: 11,
            paddingHorizontal: 13,
            color: LINEN,
            fontFamily: "DMSans_400Regular",
            fontSize: 13.9,
            minHeight: multiline ? 88 : undefined,
          },
          multiline ? ({ textAlignVertical: "top" } as TextStyle) : null,
          IS_WEB ? ({ outlineStyle: "none" } as unknown as TextStyle) : null,
        ]}
      />
      {error ? (
        <Text
          style={{
            fontFamily: "DMSans_500Medium",
            fontSize: 12,
            color: AMBER,
            marginTop: 4,
          }}
        >
          {error}
        </Text>
      ) : null}
    </View>
  );
}

function DropdownField({
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
  error?: string;
}) {
  const [open, setOpen] = useState(false);
  const borderColor = error ? AMBER : open ? AMBER : "rgba(247,244,238,0.08)";
  const bgColor = open ? "rgba(247,244,238,0.06)" : "rgba(247,244,238,0.04)";

  // Close the dropdown when clicking outside on web. Native: tapping outside
  // also closes — RN's outer Pressables eat the next tap.
  const wrapperRef = useRef<View>(null);
  useEffect(() => {
    if (!open || !IS_WEB || typeof document === "undefined") return;
    const onDown = (e: MouseEvent) => {
      const node = (wrapperRef.current as unknown as HTMLElement | null) ?? null;
      if (!node) return;
      if (node.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <View ref={wrapperRef} style={{ position: "relative", zIndex: open ? 50 : undefined }}>
      <FieldLabel>{label}</FieldLabel>
      <Pressable
        accessibilityRole="button"
        onPress={() => setOpen((v) => !v)}
        style={{
          backgroundColor: bgColor,
          borderWidth: 1,
          borderColor,
          borderRadius: 2,
          paddingVertical: 11,
          paddingHorizontal: 13,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Text
          style={{
            fontFamily: "DMSans_400Regular",
            fontSize: 13.9,
            color: value ? LINEN : "rgba(247,244,238,0.15)",
          }}
        >
          {value || placeholder}
        </Text>
        <Text style={{ color: AMBER, fontSize: 12 }}>{open ? "▴" : "▾"}</Text>
      </Pressable>

      {open ? (
        <View
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 64,
            backgroundColor: "#1a2418",
            borderWidth: 1,
            borderColor: "rgba(181,135,58,0.3)",
            borderRadius: 2,
            paddingVertical: 4,
            ...(IS_WEB ? ({ boxShadow: "0 12px 30px rgba(0,0,0,0.4)" } as ViewStyle) : {}),
          }}
        >
          {options.map((opt) => {
            const selected = opt === value;
            return (
              <Pressable
                key={opt}
                onPress={() => {
                  onChange(opt);
                  setOpen(false);
                }}
                style={({ hovered }) => [
                  {
                    paddingVertical: 9,
                    paddingHorizontal: 13,
                    backgroundColor:
                      IS_WEB && hovered
                        ? "rgba(181,135,58,0.12)"
                        : selected
                        ? "rgba(181,135,58,0.08)"
                        : "transparent",
                  },
                ]}
              >
                <Text
                  style={{
                    fontFamily: selected ? "DMSans_600SemiBold" : "DMSans_400Regular",
                    fontSize: 13.4,
                    color: selected ? LINEN : "rgba(247,244,238,0.7)",
                  }}
                >
                  {opt}
                </Text>
              </Pressable>
            );
          })}
        </View>
      ) : null}

      {error ? (
        <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 12, color: AMBER, marginTop: 4 }}>
          {error}
        </Text>
      ) : null}
    </View>
  );
}

